<template>
  <div class="workspace-shell" :class="{ detail: detailMode }">
    <section v-show="!detailMode" class="presentation-canvas">
      <DraftGameWorkspace
        v-if="effectiveDraft"
        :key="draftKey"
        :draft="effectiveDraft"
        class="presentation-content"
      />
      <component
        v-else-if="presentationComponent"
        :is="presentationComponent"
        :key="presentationKey"
        v-bind="presentationProps"
        class="presentation-content"
      />
      <Loading v-else-if="!presentationReady" label="Loading extension…" />
      <div v-else class="presentation-empty">
        <div class="empty-mark">+</div>
        <strong>No extension selected</strong>
        <span>Install or choose an extension.</span>
        <button type="button" @click="openPlugins">Open Extensions</button>
      </div>
    </section>

    <section v-if="detailMode" class="console-detail">
      <WalletTrades v-if="walletDetail" :wallet="walletDetail" @close="closeWalletTrades" />
      <ConsoleView v-else-if="store.pad?.mint" />
    </section>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import { useStorage } from '@/stores'
import WalletTrades from '@/components/wallet/trades.vue'
import ConsoleView from '@/views/Console.vue'
import DraftGameWorkspace from '@/workspaces/plugins/DraftGameWorkspace.vue'
import Loading from '@/components/_shared/Loading.vue'

const props = defineProps({
  presentationComponent: { type: [Object, Function], default: null },
  presentationProps: { type: Object, default: () => ({}) },
  presentationReady: { type: Boolean, default: false },
  ownerDraft: { type: Object, default: null },
})

const store = useStorage()
const walletDetail = ref('')
const draftPreview = ref(null)

const presentationComponent = computed(() => props.presentationComponent)
const presentationReady = computed(() => props.presentationReady)
const presentationProps = computed(() => props.presentationProps || {})
const presentationKey = computed(() => {
  const installation = presentationProps.value?.installation
  return String(installation?.id || installation?.plugin_id || installation?.plugin?.id || 'presentation')
})
const effectiveDraft = computed(() => draftPreview.value || props.ownerDraft || null)
const draftKey = computed(() => effectiveDraft.value ? `${effectiveDraft.value.pluginId}:${effectiveDraft.value.versionId}` : '')
const detailMode = computed(() => Boolean(walletDetail.value || store.pad?.mint))

window.addEventListener('aime-console-section', onConsoleSection)
window.addEventListener('aime-wallet-trades', onWalletTrades)
window.addEventListener('aime-draft-preview', onDraftPreview)
window.addEventListener('aime-draft-preview-clear', clearDraftPreview)

onBeforeUnmount(() => {
  window.removeEventListener('aime-console-section', onConsoleSection)
  window.removeEventListener('aime-wallet-trades', onWalletTrades)
  window.removeEventListener('aime-draft-preview', onDraftPreview)
  window.removeEventListener('aime-draft-preview-clear', clearDraftPreview)
})

function onConsoleSection(event) {
  const next = String(event?.detail?.section || '').trim().toLowerCase()
  if (next === 'home') walletDetail.value = ''
}

function onWalletTrades(event) {
  const wallet = String(event?.detail?.wallet || '').trim()
  if (!wallet) return
  walletDetail.value = wallet
}

function closeWalletTrades() {
  walletDetail.value = ''
}

function onDraftPreview(event) {
  const detail = event?.detail || {}
  const pluginId = String(detail.pluginId || '').trim()
  const versionId = String(detail.versionId || '').trim()
  if (!pluginId || !versionId) return
  draftPreview.value = {
    pluginId,
    versionId,
    name: String(detail.name || 'Extension').trim() || 'Extension',
    semanticVersion: String(detail.semanticVersion || '0.1.0-dev').trim() || '0.1.0-dev',
    sdkVersion: String(detail.sdkVersion || 'aime.ui-plugin.sdk.v1').trim() || 'aime.ui-plugin.sdk.v1',
  }
  walletDetail.value = ''
}

function clearDraftPreview() {
  draftPreview.value = null
}

function openPlugins() {
  window.dispatchEvent(new CustomEvent('aime-shell-workspace', { detail: { workspace: 'plugins' } }))
}
</script>

<style scoped>
.workspace-shell {
  height:100%;
  min-height:0;
  display:block;
  padding:8px 8px calc(var(--app-bottom-nav-space, 58px) + env(safe-area-inset-bottom));
  background:#0a0a0a;
  overflow:hidden;
}
.workspace-shell.detail { padding:0; }
.presentation-canvas {
  width:100%;
  height:100%;
  min-height:0;
  overflow:hidden;
  border:1px solid #222;
  border-radius:8px;
  background:#0f0f0f;
  position:relative;
}
.presentation-content { width:100%; height:100%; min-height:0; }
.presentation-empty {
  height:100%; min-height:180px;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  gap:6px; padding:24px; text-align:center; color:#7f8b9b;
}
.presentation-empty strong { color:#e5e7eb; font-size:15px; }
.presentation-empty span { max-width:320px; font-size:12px; line-height:1.4; }
.presentation-empty button {
  margin-top:8px; height:30px; padding:0 12px; border:1px solid #333; border-radius:999px;
  background:#111; color:#dbeafe; font:inherit; font-size:12px; cursor:pointer;
}
.empty-mark {
  width:34px; height:34px; display:grid; place-items:center; border:1px solid #333; border-radius:999px;
  color:#8fa1b5; font-size:20px; line-height:1;
}
.console-detail { width:100%; height:100%; min-height:0; overflow:hidden; }

@media (max-width:760px) {
  .workspace-shell { padding:6px 6px calc(var(--app-bottom-nav-space, 58px) + env(safe-area-inset-bottom)); }
  .workspace-shell.detail { padding:0; }
  .presentation-canvas { border-radius:7px; }
}
</style>
